import { CreateElement } from '@src/vue'

import { Component, Prop, Watch } from 'vue-property-decorator'
import { Mixins } from 'vue-mixin-decorator'

import { Element } from '@src/mixins/element/element'
import { Icon } from '@src/elements/icon/icon'

import { noop } from '@src/util/noop'
import { stopPropagation } from '@src/util/functions'

import './input.sass'

@Component
export class Input extends Mixins<Element>(Element) {

  // This allows you to set an initial value for the input. It will also trigger the valueChanged callback.
  @Prop({ required: false })
  initialValue: string

  // By using this prop, you can control the value from outside.
  // Use it only when you need to change the value from outside as there is no validation being applied.
  @Prop({ required: false })
  value: string

  @Prop({ required: false })
  htmlId: string

  @Prop({ required: false })
  maxValue: number

  @Prop({ required: false })
  minValue: number

  @Prop({ required: false })
  defaultValue: string

  @Prop({ required: false })
  maxLength: number

  @Prop({ required: false })
  readonly: boolean

  @Prop({ required: false, default: false })
  search: boolean

  @Prop({ required: false, default: 'text' })
  type: 'text' | 'date' | 'password' | 'number' | 'file' | 'decimal'

  @Prop({ required: false, default: 2 })
  noOfDecimals: number

  @Prop({ required: false })
  autofocus: boolean

  @Prop({ required: false })
  placeholder: string

  @Prop({ required: false })
  htmlLabel: object

  @Prop({ required: false })
  autocomplete: string

  @Prop({ required: false, default: noop })
  valueChanged: (arg: string) => void

  // use this method only for files as there is no validation done on it.
  @Prop({ required: false, default: noop })
  fileChanged: (value: string) => void

  @Prop({ required: false, default: noop })
  focusLost: (arg: string) => void

  @Prop({ required: false, default: '' })
  accept: string

  hasFocus: boolean = false

  ctrlPressed: boolean = false

  shiftPressed: boolean = false

  actualValue: string = ''

  updateProcessOngoing: boolean = false

  created () {
    if (this.initialValue) {
      this.actualValue = this.initialValue
      this.valueChanged(this.initialValue)
    }
    if (this.value || this.value === '') {
      this.updateActualValue()
    }
  }

  @Watch('value')
  updateActualValue () {
    // prevent accidental overriding due to watcher delay
    if (!this.updateProcessOngoing) {
      this.actualValue = this.value
    }
  }

  render (h: CreateElement) {
    let styleClasses = [
      ...this.elementStyles,
      ...this.inputStyles
    ]

    return (
      this.renderElement(h, styleClasses, this.disabled, [
        this.renderInput(h, this.disabled),
        this.renderIcon(h)
      ])
    )
  }

  protected renderIcon (h: CreateElement) {
    let icon = this.icon

    if (this.search) {
      if (this.actualValue) {
        icon = 'util/close'
      } else {
        icon = 'util/search'
      }
    }

    if (!icon) {
      return null
    }

    return <div class={`right-icon ${(this.search && this.actualValue) ? 'clear' : ''}`} onClick={() => {
      if (this.search) {
        this.updateProcessOngoing = false
        this.actualValue = ''
        this.valueChanged('')
      }
    }}>
      <Icon name={icon} />
    </div>
  }

  protected get inputStyles () {
    let styleClasses = ['input']

    if (this.hasFocus) {
      styleClasses.push('focus')
    }

    if (this.actualValue) {
      styleClasses.push('valid')
    }

    if (this.search) {
      styleClasses.push('search')
    }

    return styleClasses
  }

  protected renderInput (h: CreateElement, disabled: boolean, readonly: boolean = false) {
    return <div class='input-container'>
      <input ref='input' type={this.type} value={this.actualValue} autoFocus={this.autofocus} onFocus={this.onFocus}
             onKeydown={this.onKeydown} onKeyup={this.onKeyup} onInput={this.onInput} onBlur={this.onBlur} onChange={this.onFileChanged}
             disabled={disabled} readonly={this.readonly || readonly} id={this.htmlId}
             min={this.minValue} max={this.maxValue} maxlength={this.maxLength} placeholder={this.label ? '' : this.placeholder} accept={this.accept}
        // You can disable the autofill feature for an individual component by adding autocomplete="new-password" (This is because Chrome ignores the ‘off’ value)
             autocomplete={this.autocomplete ? this.autocomplete : 'new-password'} />
      {this.label ? <label class='input-description'>{this.label}</label>
        : this.htmlLabel ? <label class='input-description'>{this.htmlLabel}</label>
          : ''}
    </div>
  }

  protected onFocus (event: FocusEvent) {
    this.hasFocus = (event.target === document.activeElement)
  }

  // bound only through this method, as vue fails to update an undefined listener
  protected onFileChanged (event) {
    if (this.fileChanged) this.fileChanged(event)
  }

  protected onBlur () {
    this.hasFocus = false
    this.adjustValue()
    this.updateProcessOngoing = false
    this.focusLost(this.actualValue)
  }

  @Watch('minValue')
  protected adjustValue () {
    if (this.type !== 'number' && this.type !== 'decimal') return
    let valueInserted = this.actualValue && this.actualValue.trim() !== ''

    if (!valueInserted && this.defaultValue) {
      this.actualValue = this.defaultValue.toString()
    } else if ((!valueInserted && this.minValue) || (valueInserted && parseFloat(this.actualValue) < this.minValue)) {
      this.actualValue = this.minValue.toString()
    } else if (valueInserted && this.maxValue && parseFloat(this.actualValue) > this.maxValue) {
      this.actualValue = this.maxValue.toString()
    }
    if (this.type === 'decimal') {
      this.actualValue = this.trimDecimals(this.actualValue)
    }
    this.valueChanged(this.actualValue)
  }

  protected trimDecimals (value) {
    return parseFloat(parseFloat(value).toFixed(this.noOfDecimals)).toString()
  }

  protected onKeyup ($event) {
    if ($event.keyCode === 17 || $event.keyCode === 91) {
      this.ctrlPressed = false
      return
    }
    if ($event.keyCode === 16) {
      this.shiftPressed = false
      return
    }
  }

  protected onKeydown ($event) {
    this.updateProcessOngoing = true
    if (this.type === 'number') {
      this.removeNonNumericCharacters($event)
    } else if (this.type === 'decimal') {
      this.removeNonNumericCharacters($event, true)
    }
  }

  protected removeNonNumericCharacters ($event, allowDecimals?: boolean) {
    // ctrl on windows or command on macbook
    if ($event.keyCode === 17 || $event.keyCode === 91) {
      this.ctrlPressed = true
      return
    }
    if ($event.keyCode === 16) {
      this.shiftPressed = true
      return
    }
    let ok = false
    // if introduced symbol is : a c v x when ctrl is pressed
    if (this.ctrlPressed && ($event.keyCode === 65 || $event.keyCode === 67 || $event.keyCode === 86 || $event.keyCode === 88)) {
      ok = true
    }
    // if introduced symbol is one of the following:
    if (($event.keyCode >= 48 && $event.keyCode <= 57) // 0-9 interval
      || ($event.keyCode >= 96 && $event.keyCode <= 105) // 0-9 interval numpad
      || $event.keyCode === 8 // backspace
      || $event.keyCode === 9 // tab
      || $event.keyCode === 13 // enter
      || $event.keyCode === 37 // left arrow
      || $event.keyCode === 38 // top arrow
      || $event.keyCode === 39 // right arrow
      || $event.keyCode === 40 // bottom arrow
      || $event.keyCode === 46) { // delete
      ok = true
    }
    if (allowDecimals && ($event.keyCode === 190 || $event.keyCode === 110 || $event.keyCode === 188)) { // dot, numpad dot and comma
      ok = true
    }
    if (!ok || this.shiftPressed) {
      // stop event propagation
      this.updateProcessOngoing = false
      stopPropagation($event)
    }
  }

  protected onInput ($event) {
    this.actualValue = this.getCleanValue($event.target.value)
    this.updateProcessOngoing = false

    this.valueChanged(this.actualValue)
  }

  protected getCleanValue (value) {
    switch (this.type) {
      case 'number':
        return value.replace(/[^0-9]/g, '')
      case 'decimal':
        // remove all other characters but digits and decimal points (because safari will allow letters in numeric input)
        return this.getValidDecimal(value)
      default:
        return value
    }
  }

  protected getValidDecimal (valueToClean) {
    let value = valueToClean.replace(',', '.')
    value = value.replace(/[^0-9.]/g, '')

    while (value.startsWith('.')) {
      value = value.slice(1)
    }
    let firstDotIndex = value.indexOf('.')
    let secondDotIndex = firstDotIndex > 0 ? value.indexOf('.', firstDotIndex + 1) : - 1
    if (secondDotIndex > 0) {
      // delete everything after the second dot, including the dot itself
      value = value.slice(0, secondDotIndex)
    }
    if (value.endsWith('.')) {
      // remove dot at the end, for example: 7.
      value = value.slice(0, value.length - 1)
    }
    return value
  }

}
